import { useEffect, useRef } from 'react';

import { useSearchAndShuffleContext } from './context';
import type { HookData } from './types';
import { useTotalDecks } from './use-resize';

const MINIMUM_CARDS_PER_DECK = 2;
const MINIMUM_CARDS_IN_AVAILABLE_CARDS = 5;

type UseDealer = {
  reshuffle: () => void;
};

export const useDealer = ({ useData }: { useData: HookData }): UseDealer => {
  const {
    availableCards,
    decks,
    setDecks,
    setConsentTaken,
    setIsShuffling,
    executingAction,
    setExecutingAction,
    filter,
    setFatalErrorState,
    cardLinksEnabled,
    setLoadingStatus,
  } = useSearchAndShuffleContext();
  const seenCardIds = useRef<string[]>([]);
  const { data, fetchMore, consentTaken, fatalError } = useData(filter);
  const totalDecks = useTotalDecks();

  const dealCards = (): void => {
    if (fatalError) {
      setFatalErrorState(fatalError);
      setDecks(
        Array.from({ length: totalDecks }, (_, idx) => {
          if (idx === Math.floor(totalDecks / 2)) {
            return {
              cards: [{ type: 'Error', data: fatalError }],
              loadedCount: 0,
            };
          }
          return { cards: [], loadedCount: 0 };
        })
      );
      return;
    }

    setDecks((prevDecks) => {
      const newDecks = [...prevDecks];
      let hasUpdated = false;
      // distribute available cards to decks
      for (let idx = 0; idx < MINIMUM_CARDS_PER_DECK; idx++) {
        newDecks.forEach((deck) => {
          if (!deck.cards[idx]) {
            const card = availableCards.current.shift();
            if (card) {
              deck.cards.push(card);
              deck.loadedCount += 1;
              hasUpdated = true;
            }
          }
        });
      }

      newDecks.forEach((deck) => {
        while (
          deck.cards.length < MINIMUM_CARDS_PER_DECK &&
          availableCards.current.length > 0
        ) {
          const card = availableCards.current.shift();
          if (card) {
            deck.cards.push(card);
            hasUpdated = true;
          }
        }
      });

      // no updates to decks, return previous state
      if (!hasUpdated) {
        return prevDecks;
      }

      // if decks were updated and now we have less than MINIMUM_CARDS_IN_AVAILABLE_CARDS available cards, fetch more
      if (availableCards.current.length < MINIMUM_CARDS_IN_AVAILABLE_CARDS) {
        fetchMore();
      }

      // return new decks
      return newDecks;
    });
  };

  const reset = (): void => {
    availableCards.current = [];
    seenCardIds.current = [];
    setDecks((prevDecks) =>
      prevDecks.map((deck) => ({ ...deck, cards: [], loadedCount: 0 }))
    );
  };

  const removeCardFromDeck = (deckIndex: number, cardIndex: number): void => {
    setDecks((prevDecks) => {
      const newDecks = [...prevDecks];
      const deck = newDecks[deckIndex];
      if (deck && deck.cards[cardIndex]) {
        deck.cards.splice(cardIndex, 1);
      }
      return newDecks;
    });
  };

  const reshuffle = (): void => {
    if (executingAction) {
      return;
    }
    setExecutingAction(true);

    setIsShuffling(0);

    decks.forEach((_, deckIndex) => {
      setTimeout(
        () => {
          removeCardFromDeck(deckIndex, 0);
          setIsShuffling(
            deckIndex === decks.length - 1 ? undefined : deckIndex + 1
          );
          if (deckIndex === decks.length - 1) {
            setExecutingAction(false);
          }
        },
        200 * deckIndex + 400
      );
    });
  };

  useEffect(() => {
    reset();
  }, [filter]);

  useEffect(() => {
    if (consentTaken) {
      setConsentTaken(consentTaken);
    }
  }, [consentTaken]);

  useEffect(() => {
    if (data.length > 0) {
      setLoadingStatus('loaded');
    }
    const uniqueData = data.filter((card) => {
      const id = card?.data?.id;
      return id && !seenCardIds.current.includes(id);
    });
    seenCardIds.current.push(
      ...uniqueData
        .map((card) => card.data.id)
        .filter((id) => id !== undefined && id !== null)
    );
    availableCards.current.push(...uniqueData);
    dealCards();
  }, [data, fatalError]);

  useEffect(() => {
    cardLinksEnabled.current = true;
    dealCards();
  }, [decks]);

  // Debug helper
  // useEffect(() => {
  //   console.log({
  //     decks: decks.flatMap((d) =>
  //       d.cards.map((c) => ({
  //         type: c.data.type,
  //         id: c.data.id,
  //         card: c,
  //       }))
  //     ),
  //     availableCards: availableCards.current.flatMap((c) => ({
  //       type: c.data.type,
  //       id: c.data.id,
  //       card: c,
  //     })),
  //     seenCardIds: seenCardIds.current,
  //     rawAvailableCards: availableCards.current,
  //     rawDecks: decks,
  //   });
  // }, [decks, availableCards, seenCardIds]);

  return {
    reshuffle,
  };
};
